import api from "./apiMinty";

/**
 * SH006 — availability for the salon booking flow.
 *
 * Goes through apiMinty (the CRM api), not the ordering api: the outlet's opening hours
 * and the shared Google Calendar that holds existing appointments both live there, and
 * the member guard these endpoints sit behind is the same one SH002/SH003 already use.
 */
class SalonBookingService {
  /**
   * Every day of a month, with whether the outlet can take a booking on it.
   *
   * SH026 — `staff_id` is sent on a staff-first channel, where the stylist is already
   * chosen: the month then answers for them, so another stylist's full day no longer
   * takes a day away and the days they do not work are marked off.
   *
   * SH037 — `service_ids` narrows the month to the hours the chosen services are sold in:
   * a service on a 9 AM - noon schedule takes the days it is not offered off the calendar.
   * Left out, nothing is restricted.
   *
   * @param {{branch_code: string, month?: string, duration_min?: number, staff_id?: number,
   *          service_ids?: Array<number>}} params
   */
  getAvailableDates({ branch_code, month, duration_min, staff_id, service_ids }) {
    return api.post("member/salon/booking/available-dates", {
      branch_code,
      month,
      duration_min,
      staff_id,
      service_ids,
    });
  }

  /**
   * SH007 — the half-hour starts one date offers, grouped morning / afternoon / evening.
   *
   * SH026 — `staff_id` narrows the grid to one stylist, as in getAvailableDates().
   *
   * SH037 — `service_ids` narrows the grid to the hours those services are sold in, as in
   * getAvailableDates().
   *
   * @param {{branch_code: string, date: string, duration_min?: number, staff_id?: number,
   *          service_ids?: Array<number>}} params
   */
  getTimeSlots({ branch_code, date, duration_min, staff_id, service_ids }) {
    return api.post("member/salon/booking/time-slots", {
      branch_code,
      date,
      duration_min,
      staff_id,
      service_ids,
    });
  }

  /**
   * SH008 — the stylists this outlet offers, and who is free at the chosen moment.
   *
   * SH026 — `date` and `time` are left out on a staff-first channel, where the stylist is
   * picked before there is any moment to judge them against. The answer is then the
   * roster: everyone who works here, nobody greyed out.
   *
   * SH042 — `service_ids` narrows the roster to the stylists the merchant assigned to what
   * is being booked. Left out, the API answers with everybody, which is what it did before
   * the skill list had a reader.
   *
   * @param {{branch_code: string, date?: string, time?: string, duration_min?: number,
   *          service_ids?: Array<number>}} params
   */
  getAvailableStaff({ branch_code, date, time, duration_min, service_ids }) {
    return api.post("member/salon/booking/staff", {
      branch_code,
      date,
      time,
      duration_min,
      service_ids,
    });
  }

  /**
   * SH009 — the customer confirms, and the appointment becomes a row + a calendar event.
   *
   * Money is deliberately not sent: the API recomputes subtotal, GST and total from the
   * service lines, so what the review screen showed and what the salon charges cannot drift
   * apart. `discount_amount` is a request, not a decision — it is clamped to the subtotal.
   *
   * @param {{
   *   branch_code: string, date: string, time: string, duration_min: number,
   *   staff_id: number, staff_name?: string, services: Array<object>,
   *   promocode_id?: number, promocode_name?: string, discount_amount?: number, note?: string
   * }} params
   */
  createBooking(params) {
    return api.post("member/salon/booking/create", params);
  }

  /**
   * SH010 — "Cancel booking".
   *
   * @param {{booking_code: string}} params
   */
  cancelBooking({ booking_code }) {
    return api.post("member/salon/booking/cancel", { booking_code });
  }

  /**
   * SH010 — "View my bookings": this member's appointments, upcoming and past.
   */
  myBookings() {
    return api.post("member/salon/booking/my-bookings", {});
  }

  /**
   * SH013 — has POS taken the appointment yet, and under what receipt number.
   *
   * Polled by the Appointment created screen: nothing pushes to the phone, and POS picks the
   * order up on its own loop a few seconds after the booking is made.
   *
   * @param {{booking_code: string}} params
   */
  getPosStatus({ booking_code }) {
    return api.post("member/salon/booking/pos-status", { booking_code });
  }

  /**
   * SH017 — "Vote for stylist": rate the stylist who served this appointment.
   *
   * The chips and the free-text box are sent as one `comment` string, because that is what
   * the ratings table stores. The API re-checks every condition `can_rate` reports on, so a
   * history screen left open across the payment does not have to be trusted.
   *
   * @param {{booking_code: string, rating: number, comment?: string}} params
   */
  rateBooking({ booking_code, rating, comment }) {
    return api.post("member/salon/booking/rate", {
      booking_code,
      rating,
      comment,
    });
  }

  /**
   * SH044 — "Stylist vote": the stylists of one sale channel, with this member's own vote.
   *
   * The booking flow's own roster read (getAvailableStaff) answers "who is free"; this one
   * answers "who works here and what did I give them", which is a different question and a
   * different screen. Each card carries `rating` (the house average, or null when nobody has
   * voted) and `my_rating` (0 when this member has not).
   *
   * @param {{branch_code: string}} params
   */
  getStaffVotes({ branch_code }) {
    return api.post("member/salon/staff-votes", { branch_code });
  }

  /**
   * SH044 — cast or change this member's vote for one stylist.
   *
   * One ballot per member per stylist, wherever it was left: voting again rewrites the
   * member's latest rating of that stylist rather than adding a second, so nobody counts
   * twice in the average. When that rating came from a past appointment (rateBooking), only
   * the star moves — the comment stays, and My bookings shows the new score for that visit.
   *
   * @param {{branch_code: string, staff_id: number, rating: number}} params
   */
  voteStaff({ branch_code, staff_id, rating }) {
    return api.post("member/salon/staff-vote", {
      branch_code,
      staff_id,
      rating,
    });
  }
}

export default new SalonBookingService();
